import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
    AlertTriangle, Plus, Play, Loader2, ShieldCheck, ShieldAlert,
    Clock, Ban, CheckCircle2,
} from "lucide-react";
import api from "../../../lib/api";
import { useAuth } from "../../../lib/auth";
import { ROLES } from "../../../lib/roles";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Textarea } from "../../../components/ui/textarea";
import {
    Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "../../../components/ui/dialog";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../../components/ui/select";

const TIPOS = ["financial", "operational", "reporting", "negative", "positive"];
const FRECS = ["mensual", "trimestral", "anual", "continua"];

const RES_STYLE = {
    cumple: { badge: "badge-exito", icon: ShieldCheck, dot: "bg-[color:var(--brand-exito)]" },
    breach: { badge: "badge-error", icon: ShieldAlert, dot: "bg-[color:var(--brand-coral)]" },
    en_cure_period: { badge: "badge-gold", icon: Clock, dot: "bg-[color:var(--brand-gold)]" },
    waived: { badge: "badge-mock", icon: Ban, dot: "bg-muted-foreground" },
};

export default function CovenantsTab({ facility }) {
    const { hasRole } = useAuth();
    const [covs, setCovs] = useState([]);
    const [mediciones, setMediciones] = useState([]);
    const [breaches, setBreaches] = useState([]);
    const [cortes, setCortes] = useState([]);
    const [corteId, setCorteId] = useState("");
    const [openNew, setOpenNew] = useState(false);
    const [openWaive, setOpenWaive] = useState(null);
    const [openTrigger, setOpenTrigger] = useState(false);
    const [newForm, setNewForm] = useState({
        codigo: "", nombre: "", descripcion: "", tipo: "financial",
        formula: "", frecuencia_medicion: "mensual", cure_period_dias: 30,
    });
    const [waive, setWaive] = useState({ motivo: "", vigencia_hasta: "" });
    const [triggerForm, setTriggerForm] = useState({ motivo: "", acciones: "" });
    const [busy, setBusy] = useState(false);

    const canEdit = hasRole([ROLES.ADMINISTRADOR_FIDUCIARIO, ROLES.RIESGOS, ROLES.JURIDICO, ROLES.ADMIN_TI]);
    const canMedir = hasRole([ROLES.ADMINISTRADOR_FIDUCIARIO, ROLES.RIESGOS, ROLES.FINANZAS, ROLES.ADMIN_TI]);
    const canWaive = hasRole([ROLES.DIRECCION, ROLES.JURIDICO, ROLES.ADMIN_TI]);
    const canTrigger = hasRole([ROLES.DIRECCION, ROLES.RIESGOS, ROLES.ADMIN_TI]);

    const load = async () => {
        const [{ data: cs }, { data: ms }, { data: b }, { data: c }] = await Promise.all([
            api.get(`/facilities/${facility.id}/covenants`),
            api.get(`/facilities/${facility.id}/covenants/mediciones`),
            api.get(`/facilities/${facility.id}/covenants/breaches-abiertos`),
            api.get("/tape/cortes"),
        ]);
        setCovs(cs);
        setMediciones(ms);
        setBreaches(b);
        const filtered = (c || []).filter((x) => x.facility_id === facility.id);
        setCortes(filtered);
        if (!corteId && filtered.length) setCorteId(filtered[0].corte_id);
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [facility.id]);

    // última medición por covenant
    const lastByCov = useMemo(() => {
        const map = {};
        for (const m of mediciones) {
            if (!map[m.covenant_id] || map[m.covenant_id].fecha_medicion < m.fecha_medicion) {
                map[m.covenant_id] = m;
            }
        }
        return map;
    }, [mediciones]);

    const resumen = useMemo(() => {
        const r = { cumple: 0, breach: 0, en_cure_period: 0, waived: 0, total: covs.length };
        for (const c of covs) {
            const last = lastByCov[c.id];
            if (!last) continue;
            r[last.resultado] = (r[last.resultado] || 0) + 1;
        }
        return r;
    }, [covs, lastByCov]);

    const crear = async () => {
        setBusy(true);
        try {
            await api.post(`/facilities/${facility.id}/covenants`, newForm);
            toast.success(`Covenant ${newForm.codigo} creado`);
            setOpenNew(false);
            setNewForm({ codigo: "", nombre: "", descripcion: "", tipo: "financial",
                formula: "", frecuencia_medicion: "mensual", cure_period_dias: 30 });
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Error");
        } finally { setBusy(false); }
    };

    const medir = async () => {
        if (!corteId) return toast.error("Selecciona corte");
        setBusy(true);
        try {
            const { data } = await api.post(`/facilities/${facility.id}/covenants/medir`,
                null, { params: { corte_id: corteId } });
            toast.success(`Medición: ${data.resumen.cumple} cumple · ${data.resumen.breach + data.resumen.en_cure_period} en breach`);
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Error");
        } finally { setBusy(false); }
    };

    const doWaive = async () => {
        if (!waive.motivo || !waive.vigencia_hasta) return toast.error("Motivo y vigencia requeridos");
        setBusy(true);
        try {
            await api.post(`/covenants/${openWaive.id}/waive`, waive);
            toast.success(`Covenant ${openWaive.codigo} waived`);
            setOpenWaive(null);
            setWaive({ motivo: "", vigencia_hasta: "" });
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Error");
        } finally { setBusy(false); }
    };

    const springing = async () => {
        if (!triggerForm.motivo) return toast.error("Motivo requerido");
        setBusy(true);
        try {
            await api.post(`/facilities/${facility.id}/springing-trigger`, {
                motivo: triggerForm.motivo,
                acciones: triggerForm.acciones.split(",").map((s) => s.trim()).filter(Boolean),
            });
            toast.success("Facility movida a en_default");
            setOpenTrigger(false);
            setTriggerForm({ motivo: "", acciones: "" });
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Error");
        } finally { setBusy(false); }
    };

    return (
        <div className="space-y-6" data-testid="tab-covenants-content">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-2">
                    <AlertTriangle className="h-5 w-5 text-[color:var(--brand-dark-gold)]" />
                    <h3 className="font-display text-2xl">Covenants</h3>
                </div>
                <div className="flex gap-2 flex-wrap">
                    {canEdit && (
                        <Dialog open={openNew} onOpenChange={setOpenNew}>
                            <DialogTrigger asChild>
                                <Button variant="outline" data-testid="nuevo-covenant-btn">
                                    <Plus className="h-4 w-4 mr-1" /> Nuevo covenant
                                </Button>
                            </DialogTrigger>
                            <DialogContent className="max-w-xl">
                                <DialogHeader><DialogTitle>Nuevo covenant</DialogTitle></DialogHeader>
                                <div className="space-y-3">
                                    <div className="grid grid-cols-2 gap-2">
                                        <div><Label>Código</Label>
                                            <Input value={newForm.codigo}
                                                onChange={(e) => setNewForm({ ...newForm, codigo: e.target.value })}
                                                data-testid="cov-codigo" />
                                        </div>
                                        <div><Label>Tipo</Label>
                                            <Select value={newForm.tipo} onValueChange={(v) => setNewForm({ ...newForm, tipo: v })}>
                                                <SelectTrigger><SelectValue /></SelectTrigger>
                                                <SelectContent>
                                                    {TIPOS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                    </div>
                                    <div><Label>Nombre</Label>
                                        <Input value={newForm.nombre}
                                            onChange={(e) => setNewForm({ ...newForm, nombre: e.target.value })} />
                                    </div>
                                    <div><Label>Descripción</Label>
                                        <Textarea value={newForm.descripcion}
                                            onChange={(e) => setNewForm({ ...newForm, descripcion: e.target.value })} />
                                    </div>
                                    <div><Label>Fórmula (ej: <code>utilizacion_pct &lt;= 90</code>)</Label>
                                        <Input value={newForm.formula} className="font-mono text-xs"
                                            onChange={(e) => setNewForm({ ...newForm, formula: e.target.value })}
                                            data-testid="cov-formula" />
                                        <div className="text-[10px] text-muted-foreground mt-1">
                                            Variables: <code>utilizacion_pct</code>, <code>dpd30_pct</code>, <code>dpd60_pct</code>,
                                            <code>dpd90_pct</code>, <code>concentracion_top10</code>, <code>wac</code>
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-2 gap-2">
                                        <div><Label>Frecuencia</Label>
                                            <Select value={newForm.frecuencia_medicion}
                                                onValueChange={(v) => setNewForm({ ...newForm, frecuencia_medicion: v })}>
                                                <SelectTrigger><SelectValue /></SelectTrigger>
                                                <SelectContent>
                                                    {FRECS.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        <div><Label>Cure period (días)</Label>
                                            <Input type="number" value={newForm.cure_period_dias}
                                                onChange={(e) => setNewForm({ ...newForm, cure_period_dias: Number(e.target.value) })} />
                                        </div>
                                    </div>
                                </div>
                                <DialogFooter>
                                    <Button onClick={crear} disabled={busy} data-testid="submit-covenant">
                                        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Crear"}
                                    </Button>
                                </DialogFooter>
                            </DialogContent>
                        </Dialog>
                    )}
                    {canTrigger && breaches.some((b) => b.cure_expirado) && (
                        <Dialog open={openTrigger} onOpenChange={setOpenTrigger}>
                            <DialogTrigger asChild>
                                <Button variant="destructive" data-testid="springing-trigger-btn">
                                    <Ban className="h-4 w-4 mr-1" /> Disparar default
                                </Button>
                            </DialogTrigger>
                            <DialogContent>
                                <DialogHeader><DialogTitle>Springing trigger (default)</DialogTitle></DialogHeader>
                                <div className="space-y-3">
                                    <div className="text-xs text-[color:var(--brand-coral)] font-medium">
                                        Esto moverá la facility a <span className="badge-error">en_default</span> y bloqueará disposiciones. Requiere rol Dirección o Riesgos.
                                    </div>
                                    <div><Label>Motivo</Label>
                                        <Textarea value={triggerForm.motivo}
                                            onChange={(e) => setTriggerForm({ ...triggerForm, motivo: e.target.value })}
                                            data-testid="trigger-motivo" />
                                    </div>
                                    <div><Label>Acciones (separadas por coma)</Label>
                                        <Input value={triggerForm.acciones}
                                            placeholder="bloquear_disposiciones, acelerar_amortizacion"
                                            onChange={(e) => setTriggerForm({ ...triggerForm, acciones: e.target.value })} />
                                    </div>
                                </div>
                                <DialogFooter>
                                    <Button variant="destructive" onClick={springing} disabled={busy} data-testid="submit-trigger">
                                        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Confirmar disparo"}
                                    </Button>
                                </DialogFooter>
                            </DialogContent>
                        </Dialog>
                    )}
                </div>
            </div>

            {/* Semáforo global */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="cov-semaforo">
                {[
                    { key: "cumple", label: "Cumple", color: "text-[color:var(--brand-exito)]", Icon: ShieldCheck },
                    { key: "en_cure_period", label: "En cure period", color: "text-[color:var(--brand-dark-gold)]", Icon: Clock },
                    { key: "breach", label: "Breach", color: "text-[color:var(--brand-coral)]", Icon: ShieldAlert },
                    { key: "waived", label: "Waived", color: "text-muted-foreground", Icon: Ban },
                ].map((k) => (
                    <div key={k.key} className="kpi-card" data-testid={`kpi-${k.key}`}>
                        <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-muted-foreground">
                            <k.Icon className={`h-3 w-3 ${k.color}`} /> {k.label}
                        </div>
                        <div className={`kpi-value mt-2 ${k.color}`}>{resumen[k.key] || 0}</div>
                    </div>
                ))}
            </div>

            {/* Ejecutar medición */}
            {canMedir && (
                <div className="rounded-md border border-border bg-card p-4 flex items-end gap-3 flex-wrap" data-testid="medir-covenants-panel">
                    <div className="min-w-[240px]">
                        <Label>Corte de tape</Label>
                        <Select value={corteId} onValueChange={setCorteId}>
                            <SelectTrigger data-testid="medir-corte-select"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {cortes.map((c) => (
                                    <SelectItem key={c.corte_id} value={c.corte_id}>{c.fecha_corte} · {c.registros_validos} filas</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <Button onClick={medir} disabled={busy || !corteId || covs.length === 0}
                        data-testid="medir-btn">
                        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><Play className="h-4 w-4 mr-1" /> Medir todos</>)}
                    </Button>
                </div>
            )}

            {/* Lista covenants */}
            <div className="rounded-md border border-border bg-card overflow-hidden">
                <table className="trf-table" data-testid="covenants-table">
                    <thead>
                        <tr>
                            <th></th><th>Código</th><th>Nombre</th><th>Fórmula</th>
                            <th>Frecuencia</th><th>Cure (días)</th>
                            <th>Última medición</th><th>Cure expira</th><th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {covs.map((c) => {
                            const last = lastByCov[c.id];
                            const res = last?.resultado || "sin_medir";
                            const style = RES_STYLE[res] || { badge: "badge-gold", icon: CheckCircle2, dot: "bg-muted-foreground" };
                            const Icon = style.icon;
                            return (
                                <tr key={c.id} data-testid={`cov-row-${c.codigo}`}>
                                    <td>
                                        <div className={`h-3 w-3 rounded-full ${style.dot}`} title={res}></div>
                                    </td>
                                    <td className="font-medium">{c.codigo}</td>
                                    <td className="text-sm">
                                        {c.nombre}
                                        {c.descripcion && <div className="text-[10px] text-muted-foreground">{c.descripcion}</div>}
                                    </td>
                                    <td className="hash-cell">{c.formula}</td>
                                    <td>{c.frecuencia_medicion}</td>
                                    <td className="tabular">{c.cure_period_dias || "—"}</td>
                                    <td>
                                        {last ? (
                                            <div className="flex flex-col gap-0.5">
                                                <span className={style.badge}>
                                                    <Icon className="h-3 w-3" /> {res}
                                                </span>
                                                <span className="text-[10px] text-muted-foreground tabular">
                                                    {last.fecha_medicion?.slice(0, 16)}
                                                </span>
                                            </div>
                                        ) : (
                                            <span className="text-xs text-muted-foreground">Sin medir</span>
                                        )}
                                    </td>
                                    <td className="tabular text-xs">{last?.fecha_cure_expira || "—"}</td>
                                    <td>
                                        {canWaive && (res === "breach" || res === "en_cure_period") && (
                                            <Button size="sm" variant="ghost" onClick={() => setOpenWaive(c)}
                                                data-testid={`waive-btn-${c.codigo}`}>
                                                Waive
                                            </Button>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                        {covs.length === 0 && (
                            <tr><td colSpan={9} className="text-center py-6 text-muted-foreground">
                                Sin covenants configurados.
                            </td></tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Waiver dialog */}
            <Dialog open={!!openWaive} onOpenChange={(o) => !o && setOpenWaive(null)}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Waive covenant {openWaive?.codigo}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div><Label>Motivo (documentado)</Label>
                            <Textarea value={waive.motivo}
                                onChange={(e) => setWaive({ ...waive, motivo: e.target.value })}
                                data-testid="waive-motivo" />
                        </div>
                        <div><Label>Vigencia del waiver hasta</Label>
                            <Input type="date" value={waive.vigencia_hasta}
                                onChange={(e) => setWaive({ ...waive, vigencia_hasta: e.target.value })}
                                data-testid="waive-vigencia" />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button onClick={doWaive} disabled={busy} data-testid="submit-waive">
                            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Aplicar waiver"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {breaches.filter((b) => b.cure_expirado).length > 0 && (
                <div className="rounded-md border border-[color:var(--brand-coral)] bg-[color:var(--brand-coral)]/5 p-4 text-sm" data-testid="breaches-expirados-alert">
                    <div className="flex items-center gap-2 font-semibold text-[color:var(--brand-coral)]">
                        <ShieldAlert className="h-4 w-4" /> {breaches.filter((b) => b.cure_expirado).length} covenants con cure period expirado
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                        Puedes disparar un springing trigger para mover la facility a default.
                    </div>
                </div>
            )}
        </div>
    );
}
